import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BiometricCatalogService, type Biometric, type BiometricCreate, type BiometricUpdate } from "@/lib/api_client";
import { getAuthHeaders } from "@/lib/api";
import { fetchAll } from "./fetchAll";

export const useBiometrics = () =>
  useQuery({
    queryKey: ["biometrics"],
    queryFn: () => fetchAll<Biometric>(BiometricCatalogService.getApiBiometrics),
    staleTime: 5 * 60_000,
  });

export const useBiometric = (id: string) =>
  useQuery({
    queryKey: ["biometric", id],
    queryFn: async () => {
      const res = await BiometricCatalogService.getApiBiometricsById({ path: { id }, headers: getAuthHeaders() });
      if (!res.data) throw new Error("Not found");
      return res.data as unknown as Biometric;
    },
    enabled: !!id,
  });

export const useCreateBiometric = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: BiometricCreate) => {
      const res = await BiometricCatalogService.postApiBiometrics({ body: data, headers: getAuthHeaders() });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as Biometric;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["biometrics"] }),
  });
};

export const useUpdateBiometric = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: BiometricUpdate }) => {
      const res = await BiometricCatalogService.putApiBiometricsById({ path: { id }, body: data, headers: getAuthHeaders() });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as Biometric;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["biometrics"] });
      qc.invalidateQueries({ queryKey: ["biometric"] });
    },
  });
};
